import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import type { StepOption } from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'
import type { StepListSource } from './usePortcallStepSources'

/**
 * The keyboard/highlight behaviour shared by every list step's `Command`
 * (reka `Listbox`) listbox — the one piece of logic the per-step components
 * must not drift on. The markup is each step's own; this owns only:
 *
 * - **advance-on-↵ tracking** — a keyboard select (↵) advances the wizard, a
 *   pointer select does not. `onKeyboardAdvance` arms it on `keydown.enter`
 *   (capture), `onPointerSelect` disarms it on `pointerdown`, and `onSelect`
 *   reads-then-resets it when an option is chosen.
 * - **highlight re-nudge** — reka only re-highlights on input events, so when
 *   options arrive from an async search (or swap on step change) the first row
 *   is re-highlighted via the exposed `highlightFirstItem()` so ↵ always has a
 *   target.
 *
 * The component binds the returned `commandRef` to its `<Command ref="…">`,
 * passes its prop `source`, and gets `select(option, advance)` back through
 * `emitSelect`. `searchFirst` (vessel/principal) drives `awaitingFirstSearch`,
 * which holds the prompt before the user types.
 */
export function usePortcallStepListbox(
  source: StepListSource,
  emitSelect: (option: StepOption, advance: boolean) => void,
  opts: {
    searchFirst?: boolean
    /**
     * When this returns true (mobile soft keyboard up), ↵ dismisses the keyboard
     * instead of selecting the highlighted row. Soft keyboards have no ↑↓ arrows,
     * so the desktop "↵ selects" model reads as a surprising auto-pick; on touch
     * the user taps a row instead.
     */
    suppressEnterSelect?: () => boolean
  } = {}
) {
  // True while the pending selection is keyboard-driven (↵), which should
  // advance the wizard; a pointer press resets it so a click selects in place.
  const advanceOnSelect = ref(false)

  const awaitingFirstSearch = computed(
    () => !!opts.searchFirst && source.query.value.trim() === ''
  )

  // The Command exposes `highlightFirstItem`; the step binds this to its
  // `<Command ref="commandRef">`.
  const commandRef = useTemplateRef<{ highlightFirstItem?: () => void }>(
    'commandRef'
  )

  watch(
    () => [source.options.value, source.loading.value] as const,
    async () => {
      await nextTick()
      commandRef.value?.highlightFirstItem?.()
    }
  )

  /**
   * Arm advance-on-select for a keyboard (↵) selection. On a mobile soft keyboard
   * (`suppressEnterSelect`) there are no ↑↓ arrows, so ↵ selecting the highlighted
   * row is surprising — instead dismiss the keyboard (blur) and let the user tap a
   * row. Stopping the event in the capture phase keeps it from reaching reka's
   * `ListboxFilter`, which would otherwise turn ↵ into a selection.
   */
  function onKeyboardAdvance(event: KeyboardEvent) {
    if (opts.suppressEnterSelect?.()) {
      event.preventDefault()
      event.stopPropagation()
      ;(event.target as HTMLElement | null)?.blur()
      return
    }
    advanceOnSelect.value = true
  }
  /** A pointer press selects in place — disarm advancing. */
  function onPointerSelect() {
    advanceOnSelect.value = false
  }
  /** Select an option, advancing only when armed by the keyboard. */
  function onSelect(option: StepOption) {
    emitSelect(option, advanceOnSelect.value)
    advanceOnSelect.value = false
  }
  /** Select and always advance (double-click, or an explicit action row). */
  function selectImmediate(option: StepOption) {
    emitSelect(option, true)
  }

  // `commandRef` (bound by the component's `ref="commandRef"`) and
  // `advanceOnSelect` are internal to the watch/handlers — not returned.
  return {
    awaitingFirstSearch,
    onKeyboardAdvance,
    onPointerSelect,
    onSelect,
    selectImmediate
  }
}
